import { PasswordInput, TextInput } from '@mantine/core';
import { useState } from 'react';
import { FiLock, FiMail } from 'react-icons/fi';
import backgroundSvg from '../assests/icons/bg.svg';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SuccessToast from '../components/toast/success.toast';
import ErrorToast from '../components/toast/error.toast';
import { useAuth } from '../context/auth-context';
import ApiClient, { client } from '../utils/api-client';
import { formatLocationData } from '../utils/common';

const LoginScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [successToast, setSuccessToast] = useState({
    action: false,
    message: ''
  });
  const [errorToast, setErrorToast] = useState({ action: false, message: '' });
  const { setUserInfoOnLogin } = useAuth();
  const clientApi = new ApiClient({
    baseUrl: process.env.REACT_APP_API_URL,
    'accept-language': 'en'
  });
  const formik = useFormik({
    initialValues: {
      email: '', // Initial email value
      password: '', // Initial password value
      sourceSystem: 'ASTER'
    },
    // Define form validation schema
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email').required('Email is Required'),
      password: Yup.string().required('Password is Required')
    }),
    // Define form submission handler
    onSubmit: async (values) => {
      console.log('Values', values);
      setIsLoading(true);
      client
        .post('/users/login', values)
        .then((res: any) => {
          if (res.status && res.status === 409) {
            setErrorToast({ action: true, message: res.message });
          } else {
            setSuccessToast({
              action: true,
              message: 'Signed In Successfully !'
            });
          }

          setUserInfoOnLogin({
            data: res.data,
            accessToken: res.accessToken,
            refreshToken: res.refreshToken
          });
        })
        .catch((error: { message: any }) =>
          setErrorToast({ action: true, message: error.message })
        )
        .finally(() => setIsLoading(false));
    }
  });

  return (
    <>
      <div
        className="relative flex items-center justify-center h-screen"
        style={{
          backgroundImage: `url(${backgroundSvg})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% auto', // Adjust width to 100% and auto adjust height
          backgroundPosition: 'center',
          backgroundPositionY: 'top'
        }}
      >
        {successToast.action && (
          <SuccessToast
            title={successToast.message}
            className="absolute left-1/2 top-0 transform -translate-x-1/2 transition ease-linear slide-in-out  z-50"
          />
        )}
        {/* Show response error from server in Error Toast */}
        {errorToast.action && (
          <ErrorToast
            title={errorToast.message}
            className="absolute left-1/2 transform -translate-x-1/2 transition ease-linear slide-in-out w-96 z-50"
          />
        )}
        {/* Content Container */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center justify-center bg-white p-8 rounded-xl shadow-lg w-full max-w-lg">
            <div className="font-Poppins font-semibold text-[32px] text-[#13783C] mb-3">
              Login Your Account
            </div>
            <div className="w-full mb-2 mt-6">
              {/* Email Field */}
              <div className="flex items-center mb-2">
                <span className="text-[#13783C] text-[16px] font-medium">
                  Username/Email
                </span>
                <FiMail className="ml-2 text-[#6C6C6C] text-[16px]" />
              </div>
              <TextInput
                placeholder="Enter your email"
                value={formik.values?.email}
                onChange={(value: any) =>
                  formik.setFieldValue('email', value.target.value)
                }
                error={formik.touched.email && formik.errors.email}
                styles={{
                  input: {
                    backgroundColor: '#E4E4E4',
                    borderRadius: '12px',
                    height: '48px',
                    width: '100%'
                  }
                }}
              />
            </div>
            <div className="w-full mb-2">
              {/* Password Field */}
              <div className="flex items-center mb-2">
                <span className="text-[#13783C] text-[16px] font-medium">
                  Password
                </span>
                <FiLock className="ml-2 text-[#6C6C6C] text-[16px]" />
              </div>
              <PasswordInput
                placeholder="Enter your password"
                value={formik.values?.password}
                onChange={(event) =>
                  formik.setFieldValue('password', event.target.value)
                }
                error={formik.touched.password && formik.errors.password}
                styles={{
                  input: {
                    backgroundColor: '#E4E4E4',
                    borderRadius: '12px',
                    height: '48px',
                    width: '100%'
                  }
                }}
              />
            </div>

            <div className="mt-8 w-full gap-4 flex flex-1 flex-col">
              <button
                onClick={() => formik.handleSubmit()}
                disabled={isLoading}
                className="w-full h-[50px] rounded-[12px] font-montserrat text-[14px] text-[#FFFFFF]"
                style={{
                  background: 'linear-gradient(to left, #38BE17, #13783C)'
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/forgot-password')}
                className="w-full h-[50px] rounded-[12px] font-montserrat text-[14px] bg-[#ECECEC] text-[#2F2F2F]"
              >
                Forget Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;
